/**
 * Hero 粒子轨道：数百个发光小方块沿椭圆轨道环绕魔方（InstancedMesh，单 draw call）
 */
import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const PALETTE = [0x00e5ff, 0x007aff, 0xf5f5f7, 0x34c759, 0xffcc00];

interface Particle {
  radius: number;
  speed: number;
  phase: number;
  tilt: number;
  yaw: number;
  size: number;
  wobble: number;
}

export function OrbitParticles({ count = 300 }: { count?: number }) {
  const mesh = useRef<THREE.InstancedMesh>(null!);
  const dummy = useMemo(() => new THREE.Object3D(), []);

  const particles = useMemo<Particle[]>(() => {
    const rng = mulberry32(20260705);
    return Array.from({ length: count }, () => ({
      radius: 2.6 + rng() * 3.4,
      speed: 0.08 + rng() * 0.3,
      phase: rng() * Math.PI * 2,
      tilt: (rng() - 0.5) * 0.9,
      yaw: rng() * Math.PI * 2,
      size: 0.02 + rng() * 0.05,
      wobble: rng() * Math.PI * 2,
    }));
  }, [count]);

  const colorArray = useMemo(() => {
    const rng = mulberry32(77);
    const arr = new Float32Array(count * 3);
    const c = new THREE.Color();
    for (let i = 0; i < count; i++) {
      c.setHex(PALETTE[Math.floor(rng() * PALETTE.length)]);
      arr[i * 3] = c.r;
      arr[i * 3 + 1] = c.g;
      arr[i * 3 + 2] = c.b;
    }
    return arr;
  }, [count]);

  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    for (let i = 0; i < particles.length; i++) {
      const p = particles[i];
      const a = p.phase + t * p.speed;
      const x = Math.cos(a) * p.radius;
      const z = Math.sin(a) * p.radius * 0.72;
      const y = Math.sin(a * 2 + p.wobble) * 0.35 + Math.sin(t * 0.5 + p.wobble) * 0.15;
      dummy.position
        .set(x, y, z)
        .applyAxisAngle(new THREE.Vector3(0, 0, 1), p.tilt)
        .applyAxisAngle(new THREE.Vector3(0, 1, 0), p.yaw);
      dummy.rotation.set(a, a * 1.3, 0);
      dummy.scale.setScalar(p.size * (1 + 0.3 * Math.sin(t * 2 + p.wobble)));
      dummy.updateMatrix();
      mesh.current.setMatrixAt(i, dummy.matrix);
    }
    mesh.current.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh ref={mesh} args={[undefined, undefined, count]} frustumCulled={false}>
      <boxGeometry args={[1, 1, 1]}>
        <instancedBufferAttribute attach="attributes-color" args={[colorArray, 3]} />
      </boxGeometry>
      <meshBasicMaterial
        vertexColors
        transparent
        opacity={0.75}
        blending={THREE.AdditiveBlending}
        depthWrite={false}
      />
    </instancedMesh>
  );
}

/** 可复现随机数（避免每次渲染粒子布局跳变） */
function mulberry32(seed: number) {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
